import { type ReactElement } from 'react';
import classnames from 'classnames/bind';
import { Typography } from '@weareopenr/component-library';
import { type IMealtime } from 'src/types/mealtimes.types';
import styles from '../DesignPlatformExportFlow.module.scss';

const cx = classnames.bind(styles);

interface SelectMealtimeStepProps {
  mealtimes: IMealtime[];
  selectedMealtimeId: string | null;
  onSelectMealtime: (mealtimeId: string) => void;
}

const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const DAY_ABBREV: Record<string, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

const formatTimeString = (mealtime: IMealtime): string => {
  const enabledSchedules = mealtime.schedules.filter((s) => s.enabled);

  if (enabledSchedules.length === 0) {
    return 'No schedule set';
  }

  // Group schedules by time
  const timeGroups: Record<string, string[]> = {};
  enabledSchedules.forEach((schedule) => {
    const timeKey = `${schedule.start_time}-${schedule.end_time}`;
    if (!timeGroups[timeKey]) {
      timeGroups[timeKey] = [];
    }
    timeGroups[timeKey].push(schedule.day_of_week);
  });

  // Check if all have the same time
  const uniqueTimes = Object.keys(timeGroups);
  if (uniqueTimes.length === 1) {
    const [startTime, endTime] = uniqueTimes[0].split('-');
    return `${startTime.slice(0, 5)} - ${endTime.slice(0, 5)}`;
  }

  // Group consecutive days
  const formatDayRange = (days: string[], time: string): string => {
    const sortedDays = days.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
    const [startTime, endTime] = time.split('-');

    if (sortedDays.length === 1) {
      return `${DAY_ABBREV[sortedDays[0]]} ${startTime.slice(0, 5)}-${endTime.slice(0, 5)}`;
    }

    // Check if days are consecutive
    let isConsecutive = true;
    for (let i = 1; i < sortedDays.length; i += 1) {
      if (DAY_ORDER.indexOf(sortedDays[i]) - DAY_ORDER.indexOf(sortedDays[i - 1]) !== 1) {
        isConsecutive = false;
        break;
      }
    }

    if (isConsecutive) {
      return `${DAY_ABBREV[sortedDays[0]]}–${DAY_ABBREV[sortedDays[sortedDays.length - 1]]} ${startTime.slice(0, 5)}-${endTime.slice(0, 5)}`;
    }

    return sortedDays.map((d) => DAY_ABBREV[d]).join(', ') + ` ${startTime.slice(0, 5)}-${endTime.slice(0, 5)}`;
  };

  return Object.entries(timeGroups)
    .map(([time, days]) => formatDayRange(days, time))
    .join(' · ');
};

export const SelectMealtimeStep = ({
  mealtimes,
  selectedMealtimeId,
  onSelectMealtime,
}: SelectMealtimeStepProps): ReactElement => (
  <div className={styles['step-content']}>
    <div className={styles['mealtime-list']}>
      {mealtimes.map((mealtime) => {
        const isSelected = selectedMealtimeId === mealtime.mealtime_id;
        return (
          <button
            key={mealtime.mealtime_id}
            type="button"
            className={cx('mealtime-item', { 'mealtime-item-selected': isSelected })}
            onClick={() => onSelectMealtime(mealtime.mealtime_id)}
          >
            <div className={styles['mealtime-radio']}>
              <div className={cx('radio-outer', { 'radio-outer-selected': isSelected })}>
                {isSelected && <div className={styles['radio-inner']} />}
              </div>
            </div>
            <div className={styles['mealtime-info']}>
              <Typography size="medium" weight="bold">
                {mealtime.name}
              </Typography>
              <Typography size="xsmall" className={styles['mealtime-schedule']}>
                {formatTimeString(mealtime)}
              </Typography>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);
